// ─── FIREBASE CONFIG ──────────────────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyBkFz-2EZ-4yahMAIoslvw-a0955IIzkk8",
  authDomain: "orangepay-appt.firebaseapp.com",
  projectId: "orangepay-appt",
  storageBucket: "orangepay-appt.firebasestorage.app",
  messagingSenderId: "988399242051",
  appId: "1:988399242051:web:a82b1e845efcee5893bdf6",
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// ─── COLLECTIONS ──────────────────────────────────────────────────────────────
// accounts  → /accounts/{phone}
// transactions → /transactions/{id}   (field: ownerPhone)
// settings  → /settings/global  (feeRate, etc.)

// ─── COMPTES ──────────────────────────────────────────────────────────────────

/** Lire tous les comptes */
export const fetchAccounts = async () => {
  const snap = await getDocs(collection(db, "accounts"));
  return snap.docs.map((d) => ({ ...d.data(), phone: d.id }));
};

/** Lire un compte par téléphone */
export const fetchAccount = async (phone) => {
  const snap = await getDoc(doc(db, "accounts", phone));
  return snap.exists() ? { ...snap.data(), phone: snap.id } : null;
};

/** Créer ou mettre à jour un compte (upsert) */
export const saveAccount = async (account) => {
  const { phone, ...data } = account;
  await setDoc(doc(db, "accounts", phone), data, { merge: true });
};

/** Supprimer un compte */
export const deleteAccount = async (phone) => {
  await deleteDoc(doc(db, "accounts", phone));
};

/** Supprimer TOUTES les transactions d'un compte (ownerPhone === phone) */
export const deleteAccountTransactions = async (phone) => {
  const q = query(
    collection(db, "transactions"),
    where("ownerPhone", "==", phone)
  );
  const snap = await getDocs(q);
  const deletions = snap.docs.map((d) => deleteDoc(doc(db, "transactions", d.id)));
  await Promise.all(deletions);
};

/** Mettre à jour un champ précis d'un compte */
export const updateAccount = async (phone, fields) => {
  await updateDoc(doc(db, "accounts", phone), fields);
};

// ─── TRANSACTIONS ─────────────────────────────────────────────────────────────

/** Lire les transactions d'un compte (triées par date desc) */
export const fetchTransactions = async (phone) => {
  const q = query(
    collection(db, "transactions"),
    where("ownerPhone", "==", phone)
  );
  const snap = await getDocs(q);
  const txs = snap.docs.map((d) => ({ ...d.data(), _firestoreId: d.id }));
  txs.sort((a, b) => (b.id || 0) - (a.id || 0));
  return txs;
};

/** Lire TOUTES les transactions en attente (admin) */
export const fetchAllPendingTxs = async () => {
  const q = query(
    collection(db, "transactions"),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  const txs = snap.docs.map((d) => ({ ...d.data(), _firestoreId: d.id }));
  txs.sort((a, b) => (b.id || 0) - (a.id || 0));
  return txs;
};

/** Ajouter une transaction */
export const addTransaction = async (tx) => {
  await addDoc(collection(db, "transactions"), tx);
};

/** Mettre à jour le statut d'une transaction */
export const updateTransaction = async (firestoreId, fields) => {
  await updateDoc(doc(db, "transactions", firestoreId), fields);
};

// ─── SETTINGS GLOBAUX ─────────────────────────────────────────────────────────

/** Lire le taux de frais */
export const fetchFeeRate = async () => {
  const snap = await getDoc(doc(db, "settings", "global"));
  return snap.exists() ? snap.data().feeRate ?? 0.03 : 0.03;
};

/** Sauvegarder le taux de frais */
export const saveFeeRate = async (rate) => {
  await setDoc(doc(db, "settings", "global"), { feeRate: rate }, { merge: true });
};

/** Sauvegarder la policy de transfert d'un compte */
export const savePolicy = async (phone, policy) => {
  await setDoc(doc(db, "settings", "global"), { [`policy_${phone}`]: policy }, { merge: true });
};

/** Écoute temps réel des settings globaux (feeRate + policies) */
export const listenSettings = (callback) => {
  return onSnapshot(doc(db, "settings", "global"), (snap) => {
    callback(snap.exists() ? snap.data() : {});
  });
};

// ─── ÉCOUTE TEMPS RÉEL (optionnel) ───────────────────────────────────────────

/** Écoute les transactions d'un compte en temps réel */
export const listenTransactions = (phone, callback) => {
  const q = query(
    collection(db, "transactions"),
    where("ownerPhone", "==", phone)
  );
  return onSnapshot(q, (snap) => {
    const txs = snap.docs.map((d) => ({ ...d.data(), _firestoreId: d.id }));
    // Tri côté client par id décroissant (évite l'index composite Firestore)
    txs.sort((a, b) => (b.id || 0) - (a.id || 0));
    callback(txs);
  });
};

/** Écoute tous les comptes en temps réel */
export const listenAccounts = (callback) => {
  return onSnapshot(collection(db, "accounts"), (snap) => {
    callback(snap.docs.map((d) => ({ ...d.data(), phone: d.id })));
  });
};
